/**
 * 📊 엑셀 미리보기 (v2.270.0) — 받은 파일을 화면에서 표로 그릴 수 있게 바꾼다.
 *
 * 미리보기 창(`PdfPreviewModal`)이 PDF·그림처럼 엑셀(xlsx·xlsm·xls·csv)도 띄운다.
 *   · 서버는 엑셀을 늘 「내려받기」로만 준다(attachments `INLINE_SAFE_MIMES` 에 없음) — 그래도 fetch 로 받아
 *     **화면 안에서** 읽으면 된다. 파일을 브라우저가 직접 여는 게 아니라 글자만 꺼내 표로 그린다.
 *   · 읽기는 SheetJS 0.20.3(frontend/vendor 의 tgz). ⛔0.18.5 는 일부러 만든 파일을 읽을 때
 *     prototype 오염(CVE-2023-30533)·ReDoS(CVE-2024-22363)가 있어 남이 올린 파일을 미리 보기엔 위험했다.
 *   · 이 파일은 순수 함수만 둔다 — SheetJS 모듈을 인자로 받아서 그물(scripts/check-sheet-preview.ts)이
 *     node 에서 같은 함수를 돌린다.
 *
 * 화면 규칙
 *   · 값은 엑셀이 보여 주던 글자(서식 적용된 `w`) 그대로 — 날짜·₩·쉼표가 엑셀과 같다.
 *   · 숨은 열·행·시트는 엑셀처럼 기본은 숨기고 [숨은 열·행 보기]로 펼친다(협약금액 같은 칸이 숨은 열에 있다).
 *   · 너무 큰 시트는 앞쪽만(행 MAX_PARSE_ROWS · 열 MAX_COLS) — 다 보려면 저장해서 연다.
 */

/** 엑셀로 볼 파일인가 — 이름 끝 또는 형식(mime) */
export function isSheetFile(filename: string, mime?: string | null): boolean {
  const lower = (filename || '').toLowerCase();
  const m = (mime ?? '').toLowerCase();
  return /\.(xlsx|xlsm|xls|csv)$/.test(lower)
    || m.includes('spreadsheetml') || m.includes('ms-excel') || m === 'text/csv';
}

/** CSV 인가 (글자 인코딩을 따로 풀어야 한다) */
export function isCsvFile(filename: string, mime?: string | null): boolean {
  return /\.csv$/i.test(filename || '') || (mime ?? '').toLowerCase() === 'text/csv';
}

/** 시트 하나에서 읽을 최대 행 수 — 넘으면 앞쪽만 읽고 「더 있음」을 알린다 */
export const MAX_PARSE_ROWS = 2000;
/** 그릴 최대 열 수 */
export const MAX_COLS = 100;
/** 받는 파일 크기 상한 (첨부 상한 20MB 보다 조금 넉넉히) */
export const MAX_BYTES = 25 * 1024 * 1024;

export interface SheetCell {
  text: string;
  /** 숫자·날짜면 오른쪽 맞춤 */
  num: boolean;
  /** 칠한 색 (#RRGGBB) — 흰색·없음은 null */
  fill: string | null;
  /**
   * 바탕이 어두운 칸 → 글자를 흰색으로. SheetJS 무료판은 글자색을 안 읽어 준다 —
   *   운영 유지보수 리스트는 고객사·청구업체 열이 검은 바탕(흰 글씨)이라 그대로 그리면 검은 칸만 보였다.
   */
  dark: boolean;
}

/** 칠한 색이 어두운가 (밝기 0.5 미만) */
export function isDarkFill(hex: string | null): boolean {
  if (!hex) return false;
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
}

export interface SheetMerge { r1: number; c1: number; r2: number; c2: number }

export interface PreviewSheet {
  name: string;
  /** 엑셀에서 숨긴 시트 */
  hidden: boolean;
  /** 실제 행 번호(0부터) — 값이 있는 마지막 행까지 */
  rowCount: number;
  colCount: number;
  /** cells[r][c] — 빈 칸은 null */
  cells: (SheetCell | null)[][];
  /** 열 폭(px) */
  colWidths: number[];
  hiddenCols: boolean[];
  hiddenRows: boolean[];
  merges: SheetMerge[];
  /** 앞쪽만 읽었나 (행·열) */
  truncatedRows: boolean;
  truncatedCols: boolean;
  /** 파일 전체 행·열 수 (앞쪽만 읽었을 때 안내용) */
  fullRows: number;
  fullCols: number;
}

export interface PreviewBook {
  sheets: PreviewSheet[];
}

/** SheetJS 에서 쓰는 것만 — 테스트에서 node 쪽 모듈을 그대로 넘긴다 */
export interface SheetLib {
  read: (data: unknown, opts: Record<string, unknown>) => {
    SheetNames: string[];
    Sheets: Record<string, Record<string, unknown>>;
    Workbook?: { Sheets?: { Hidden?: number }[] };
  };
  utils: {
    decode_range: (ref: string) => { s: { r: number; c: number }; e: { r: number; c: number } };
    decode_cell: (addr: string) => { r: number; c: number };
  };
}

/** CSV 글자 풀기 — UTF-8(BOM 포함)이 아니면 한글 엑셀이 저장한 EUC-KR(CP949)로 본다 */
export function decodeCsv(bytes: Uint8Array): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes).replace(/^﻿/, '');
  } catch {
    return new TextDecoder('euc-kr').decode(bytes);
  }
}

/**
 * 엑셀 열 폭 → px. 엑셀 폭 단위(글자 수 width)를 글자 하나 8px 로 친다.
 *   ⚠SheetJS 가 주는 wpx 는 글자 하나를 7px 로 잡아(맑은 고딕 11pt 보다 좁다) 「확정」 같은 두 글자 칸이
 *     좁아져 한 글자씩 줄이 바뀌었다(운영 유지보수 리스트 실측 · 행 높이 275px) — width 를 먼저 쓴다.
 *   열 폭이 없으면 엑셀 기본 8.43글자 ≈ 72px.
 */
function colPx(col: { wpx?: number; wch?: number; width?: number } | undefined): number {
  let px = 72;
  if (col?.width) px = Math.round(col.width * 8 + 5);
  else if (col?.wch) px = Math.round(col.wch * 8 + 5);
  else if (col?.wpx) px = Math.round((col.wpx * 8) / 7);
  return Math.max(24, Math.min(480, px));
}

const HEX6 = /^[0-9A-F]{6}$/i;

/** 칠한 색 — solid 채우기의 RGB 만(테마 색은 SheetJS 무료판이 RGB 로 못 풀어 준다) */
function fillOf(s: unknown): string | null {
  const st = s as { patternType?: string; fgColor?: { rgb?: string } } | undefined;
  if (!st || st.patternType !== 'solid') return null;
  let rgb = st.fgColor?.rgb ?? '';
  if (rgb.length === 8) rgb = rgb.slice(2); // ARGB → RGB
  if (!HEX6.test(rgb) || rgb.toUpperCase() === 'FFFFFF') return null;
  return `#${rgb}`;
}

function cellOf(raw: unknown): SheetCell | null {
  const c = raw as { t?: string; v?: unknown; w?: string; s?: unknown } | undefined;
  if (!c || c.t === 'z') {
    const fill = c ? fillOf(c.s) : null;
    return fill ? { text: '', num: false, fill, dark: isDarkFill(fill) } : null;
  }
  let text: string;
  if (typeof c.w === 'string') text = c.w;
  else if (c.t === 'b') text = c.v ? 'TRUE' : 'FALSE';
  else if (c.v instanceof Date) text = c.v.toISOString().slice(0, 10);
  else text = c.v == null ? '' : String(c.v);
  const fill = fillOf(c.s);
  if (text === '' && !fill) return null;
  return { text, num: c.t === 'n' || c.t === 'd', fill, dark: isDarkFill(fill) };
}

/**
 * 파일 → 미리보기용 표.
 *   data: xlsx·xls 는 바이트(Uint8Array), csv 는 decodeCsv 로 푼 글자.
 */
export function parseSheetPreview(XLSX: SheetLib, data: Uint8Array | string): PreviewBook {
  const isText = typeof data === 'string';
  const wb = XLSX.read(data, {
    type: isText ? 'string' : 'array',
    cellStyles: true,
    cellDates: false,
    cellFormula: false,
    cellHTML: false,
    sheetRows: MAX_PARSE_ROWS + 1,
    // CSV 는 글자 그대로 — 「2026-01」·「00123」 같은 글을 날짜·숫자로 바꾸지 않는다
    raw: isText,
  });
  const sheets: PreviewSheet[] = [];
  wb.SheetNames.forEach((name, idx) => {
    const ws = wb.Sheets[name] ?? {};
    const hidden = (wb.Workbook?.Sheets?.[idx]?.Hidden ?? 0) > 0;
    const ref = ws['!ref'] as string | undefined;
    const fullRef = (ws['!fullref'] as string | undefined) ?? ref;
    const empty: PreviewSheet = {
      name, hidden, rowCount: 0, colCount: 0, cells: [], colWidths: [], hiddenCols: [], hiddenRows: [],
      merges: [], truncatedRows: false, truncatedCols: false, fullRows: 0, fullCols: 0,
    };
    if (!ref) { sheets.push(empty); return; }
    const full = XLSX.utils.decode_range(fullRef!);
    const merges = ((ws['!merges'] as { s: { r: number; c: number }; e: { r: number; c: number } }[] | undefined) ?? [])
      .map((m) => ({ r1: m.s.r, c1: m.s.c, r2: m.e.r, c2: m.e.c }));

    // 값이 있는 칸으로 실제 넓이를 잰다 — !ref 는 서식만 있는 빈 칸까지 넓게 잡혀 있곤 하다
    let maxR = -1;
    let maxC = -1;
    const found: { r: number; c: number; cell: SheetCell }[] = [];
    for (const addr of Object.keys(ws)) {
      if (addr.startsWith('!')) continue;
      const cell = cellOf(ws[addr]);
      if (!cell) continue;
      const { r, c } = XLSX.utils.decode_cell(addr);
      if (r >= MAX_PARSE_ROWS) continue;
      found.push({ r, c, cell });
      if (cell.text !== '') {
        if (r > maxR) maxR = r;
        if (c > maxC) maxC = c;
      }
    }
    for (const m of merges) {
      if (m.r1 < MAX_PARSE_ROWS && m.r1 <= maxR) maxC = Math.max(maxC, m.c2);
    }
    if (maxR < 0) { sheets.push({ ...empty, fullRows: full.e.r + 1, fullCols: full.e.c + 1 }); return; }
    const rowCount = maxR + 1;
    const colCount = Math.min(maxC + 1, MAX_COLS);
    const cells: (SheetCell | null)[][] = Array.from({ length: rowCount }, () => Array<SheetCell | null>(colCount).fill(null));
    for (const f of found) {
      if (f.r < rowCount && f.c < colCount) cells[f.r]![f.c] = f.cell;
    }
    const cols = (ws['!cols'] as ({ wpx?: number; wch?: number; width?: number; hidden?: boolean } | undefined)[] | undefined) ?? [];
    const rows = (ws['!rows'] as ({ hidden?: boolean } | undefined)[] | undefined) ?? [];
    sheets.push({
      name,
      hidden,
      rowCount,
      colCount,
      cells,
      colWidths: Array.from({ length: colCount }, (_, c) => colPx(cols[c])),
      hiddenCols: Array.from({ length: colCount }, (_, c) => !!cols[c]?.hidden),
      hiddenRows: Array.from({ length: rowCount }, (_, r) => !!rows[r]?.hidden),
      merges: merges.filter((m) => m.r1 < rowCount && m.c1 < colCount)
        .map((m) => ({ ...m, r2: Math.min(m.r2, rowCount - 1), c2: Math.min(m.c2, colCount - 1) })),
      truncatedRows: full.e.r + 1 > MAX_PARSE_ROWS,
      truncatedCols: maxC + 1 > MAX_COLS,
      fullRows: full.e.r + 1,
      fullCols: full.e.c + 1,
    });
  });
  return { sheets };
}

/** 엑셀 열 이름 (0 → A, 26 → AA) */
export function colName(c: number): string {
  let s = '';
  let n = c + 1;
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/** 읽기 실패 문구 — 암호 걸린 파일은 따로 알린다 */
export function sheetErrorText(e: unknown): string {
  const msg = String((e as Error)?.message ?? e ?? '');
  if (/password|encrypt/i.test(msg)) return '암호가 걸린 엑셀은 화면에서 미리 볼 수 없습니다. 저장해서 엑셀로 열어 주세요.';
  return `엑셀 내용을 읽지 못했습니다${msg ? ` (${msg.slice(0, 120)})` : ''}. 저장해서 엑셀로 열어 주세요.`;
}
